import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { useStudentAuth } from '@/context/StudentAuthContext';
import { useAssetContext } from '@/context/AssetContext';
import { logCalculatorUsed } from '@/utils/activityLogger';
import { 
  ArrowLeft,
  Calculator,
  GraduationCap,
  Globe,
  ChevronRight,
  Info,
  TrendingUp,
  Target,
  Award,
  Sparkles,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';

const API_URL = window.location.origin;

// Board options
const BOARDS = [
  { id: "cbse", name: "CBSE", description: "Central Board of Secondary Education" },
  { id: "icse", name: "ICSE/ISC", description: "Indian Certificate of Secondary Education" },
  { id: "ib", name: "IB", description: "International Baccalaureate" },
  { id: "igcse", name: "IGCSE/Cambridge", description: "Cambridge International" },
  { id: "state_board", name: "State Board", description: "Various State Boards" },
  { id: "other", name: "Other", description: "Other boards" }
];

// University tiers
const UNIVERSITY_TIERS = [
  { id: "iit_nit", name: "IIT / NIT / IIIT", description: "Premier technical institutes" },
  { id: "central", name: "Central University", description: "DU, JNU, BHU, etc." },
  { id: "state_top", name: "Top State University", description: "Leading state universities" },
  { id: "state", name: "State University", description: "Regular state universities" },
  { id: "private_top", name: "Top Private", description: "BITS, VIT, SRM, Manipal, etc." },
  { id: "private", name: "Private University", description: "Other private universities" },
  { id: "deemed", name: "Deemed University", description: "Deemed-to-be universities" },
  { id: "autonomous", name: "Autonomous College", description: "Autonomous colleges" }
];

// Destination countries
const DESTINATIONS = [
  { id: "australia", name: "Australia", flag: "🇦🇺", system: "7.0 GPA" },
  { id: "canada", name: "Canada", flag: "🇨🇦", system: "4.0 GPA" },
  { id: "dubai", name: "Dubai (UAE)", flag: "🇦🇪", system: "4.0 GPA" },
  { id: "germany", name: "Germany", flag: "🇩🇪", system: "1.0-5.0" },
  { id: "ireland", name: "Ireland", flag: "🇮🇪", system: "Honours" },
  { id: "new_zealand", name: "New Zealand", flag: "🇳🇿", system: "9.0 GPA" },
  { id: "uk", name: "UK", flag: "🇬🇧", system: "Honours" },
  { id: "usa", name: "USA", flag: "🇺🇸", system: "4.0 GPA" }
];

const GPACalculatorPage = () => {
  const { isDark } = useTheme();
  const { user } = useStudentAuth();
  const { getLogo } = useAssetContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Check if coming from AI Finder
  const fromAIFinder = searchParams.get('source') === 'ai-finder';
  
  // Form state
  const [level, setLevel] = useState('bachelors'); // bachelors or masters
  const [board, setBoard] = useState('cbse');
  const [universityTier, setUniversityTier] = useState('state');
  const [destination, setDestination] = useState('usa');
  
  // Score inputs - will be shown based on board selection
  const [percentage, setPercentage] = useState('');
  const [cgpa, setCgpa] = useState('');
  const [cgpaScale, setCgpaScale] = useState('10');
  const [ibPoints, setIbPoints] = useState('');
  const [igcseGrade, setIgcseGrade] = useState('');
  
  // Result state
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Get score input type and range based on board
  const getBoardScoreConfig = (selectedBoard) => {
    switch (selectedBoard) {
      case 'ib':
        return {
          type: 'ib_points',
          label: 'Your IB Points',
          placeholder: 'e.g., 38',
          min: 24,
          max: 45,
          hint: 'Enter IB Diploma points (24-45)',
          step: 1
        };
      case 'igcse':
        return {
          type: 'percentage',
          label: 'Your Percentage / Grade Points',
          placeholder: 'e.g., 85',
          min: 0,
          max: 100,
          hint: 'Enter percentage equivalent (A*=90+, A=80-89, B=70-79...)',
          step: 1
        };
      case 'cbse':
        return {
          type: 'percentage',
          label: 'Your Percentage',
          placeholder: 'e.g., 85',
          min: 0,
          max: 100,
          hint: 'Enter your Class 12 percentage (0-100)',
          step: 0.1
        };
      case 'icse':
        return {
          type: 'percentage',
          label: 'Your Percentage',
          placeholder: 'e.g., 88',
          min: 0,
          max: 100,
          hint: 'Enter your ISC percentage (0-100)',
          step: 0.1
        };
      case 'state_board':
        return {
          type: 'percentage',
          label: 'Your Percentage',
          placeholder: 'e.g., 80',
          min: 0,
          max: 100,
          hint: 'Enter your board percentage (0-100)',
          step: 0.1
        };
      default:
        return {
          type: 'percentage',
          label: 'Your Percentage',
          placeholder: 'e.g., 75',
          min: 0,
          max: 100,
          hint: 'Enter percentage (0-100)',
          step: 0.1
        };
    }
  };
  
  const scoreConfig = getBoardScoreConfig(board);
  
  // Clear score when board changes
  const handleBoardChange = (newBoard) => {
    setBoard(newBoard);
    setPercentage('');
    setIbPoints('');
    setCgpa('');
  };

  const handleCalculate = async () => {
    // Validation based on board's score config type
    if (scoreConfig.type === 'ib_points') {
      if (!ibPoints || isNaN(ibPoints)) {
        toast.error('Please enter valid IB points');
        return;
      }
      if (parseInt(ibPoints) < scoreConfig.min || parseInt(ibPoints) > scoreConfig.max) {
        toast.error(`IB points must be between ${scoreConfig.min} and ${scoreConfig.max}`);
        return;
      }
    } else if (scoreConfig.type === 'percentage') {
      if (!percentage || isNaN(percentage)) {
        toast.error('Please enter a valid percentage');
        return;
      }
      if (parseFloat(percentage) < scoreConfig.min || parseFloat(percentage) > scoreConfig.max) {
        toast.error(`Percentage must be between ${scoreConfig.min} and ${scoreConfig.max}`);
        return;
      }
    }

    setLoading(true);
    try {
      const payload = {
        level,
        destination,
        board: level === 'bachelors' ? board : undefined,
        university_tier: level === 'masters' ? universityTier : undefined
      };

      // Set score based on board type
      if (scoreConfig.type === 'ib_points') {
        payload.ib_points = parseInt(ibPoints);
      } else {
        payload.percentage = parseFloat(percentage);
      }

      const res = await fetch(`${API_URL}/api/gpa/convert`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        const data = await res.json();
        setResult(data);
        
        // Log activity: GPA calculated
        if (user?.user_id) {
          logCalculatorUsed(user.user_id, 'gpa', {
            level,
            destination,
            board: level === 'bachelors' ? board : undefined,
            university_tier: level === 'masters' ? universityTier : undefined,
            percentage: parseFloat(percentage) || null,
            ib_points: parseInt(ibPoints) || null
          }, {
            gpa: data.destination_gpa,
            normalized_percentage: data.normalized_percentage
          });
        }
        
        // Save calculated GPA for AI Finder if coming from there
        if (fromAIFinder) {
          // Get the scale from the selected destination
          const selectedDest = DESTINATIONS.find(d => d.id === destination);
          const scaleFromDest = selectedDest?.system || '4.0';
          // Extract numeric scale (e.g., "4.0 GPA" -> "4.0", "7.0 GPA" -> "7.0")
          const scaleMatch = scaleFromDest.match(/[\d.]+/);
          const numericScale = scaleMatch ? scaleMatch[0] : '4.0';
          
          // Extract the GPA value from destination_gpa object
          // Handles various formats:
          // - USA/Canada: { gpa_4_scale: 3.7 }
          // - Australia: { gpa_7_scale: 5.5 }
          // - UK/Ireland: { classification: "First Class Honours", gpa_equivalent: "3.7-4.0" }
          // - Germany: { german_grade: 1.5 }
          // - NZ: { gpa_9_scale: 7.5 }
          let gpaValue = data.destination_gpa;
          let extractedScale = numericScale;
          
          if (typeof gpaValue === 'object' && gpaValue !== null) {
            // Check for UK/Ireland format (has gpa_equivalent like "3.7-4.0")
            if (gpaValue.gpa_equivalent) {
              // Extract the lower value from range "3.7-4.0" -> 3.7 (more conservative)
              const rangeMatch = gpaValue.gpa_equivalent.match(/([\d.]+)-([\d.]+)/);
              if (rangeMatch) {
                gpaValue = parseFloat(rangeMatch[1]); // Use lower end of range (conservative)
              } else {
                const singleMatch = gpaValue.gpa_equivalent.match(/[\d.]+/);
                gpaValue = singleMatch ? parseFloat(singleMatch[0]) : null;
              }
              extractedScale = '4'; // UK equivalents are on 4.0 scale
            } else {
              // Standard numeric GPA objects - extract value AND set correct scale
              if (gpaValue.gpa_4_scale !== undefined) {
                gpaValue = gpaValue.gpa_4_scale;
                extractedScale = '4';
              } else if (gpaValue.gpa_7_scale !== undefined) {
                gpaValue = gpaValue.gpa_7_scale;
                extractedScale = '7';
              } else if (gpaValue.gpa_9_scale !== undefined) {
                gpaValue = gpaValue.gpa_9_scale;
                extractedScale = '9';
              } else if (gpaValue.german_grade !== undefined) {
                gpaValue = gpaValue.german_grade;
                extractedScale = '5'; // German uses 1.0-5.0 scale
              } else if (gpaValue.gpa !== undefined) {
                gpaValue = gpaValue.gpa;
                // Keep extractedScale from destination
              } else {
                gpaValue = Object.values(gpaValue).find(v => typeof v === 'number');
              }
            }
          }
          
          if (gpaValue && !isNaN(gpaValue)) {
            localStorage.setItem('calculated_gpa', JSON.stringify({
              gpa: gpaValue,
              scale: extractedScale,
              percentage: data.normalized_percentage,
              destination: data.destination
            }));
          }
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Conversion failed');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to calculate GPA');
    } finally {
      setLoading(false);
    }
  };

  const getCompetitivenessColor = (color) => {
    const colors = {
      emerald: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      blue: 'bg-blue-100 text-blue-700 border-blue-200',
      cyan: 'bg-cyan-100 text-cyan-700 border-cyan-200',
      amber: 'bg-amber-100 text-amber-700 border-amber-200',
      orange: 'bg-orange-100 text-orange-700 border-orange-200',
      red: 'bg-red-100 text-red-700 border-red-200'
    };
    return colors[color] || colors.blue;
  };

  const formatGPA = (gpa) => {
    if (!gpa) return '';
    
    // USA/Canada style
    if (gpa.gpa_4_scale !== undefined) {
      return (
        <div className="text-center">
          <div className="text-5xl font-bold text-ux-violet mb-1">{gpa.gpa_4_scale}</div>
          <div className="text-sm text-gray-500">on {gpa.scale} scale</div>
        </div>
      );
    }
    
    // UK/Ireland style
    if (gpa.classification) {
      return (
        <div className="text-center">
          <div className="text-3xl font-bold text-ux-violet mb-1">{gpa.code}</div>
          <div className="text-lg font-medium">{gpa.classification}</div>
          <div className="text-sm text-gray-500">≈ {gpa.gpa_equivalent} GPA</div>
        </div>
      );
    }
    
    // Australia style
    if (gpa.gpa_7_scale !== undefined) {
      return (
        <div className="text-center">
          <div className="text-5xl font-bold text-ux-violet mb-1">{gpa.gpa_7_scale}</div>
          <div className="text-lg font-medium">{gpa.grade} - {gpa.description}</div>
          <div className="text-sm text-gray-500">on {gpa.scale} scale</div>
        </div>
      );
    }
    
    // Germany style
    if (gpa.german_grade !== undefined) {
      return (
        <div className="text-center">
          <div className="text-5xl font-bold text-ux-violet mb-1">{gpa.german_grade}</div>
          <div className="text-lg font-medium">{gpa.description}</div>
          <div className="text-sm text-gray-500">{gpa.scale}</div>
        </div>
      );
    }
    
    // NZ style
    if (gpa.gpa_9_scale !== undefined) {
      return (
        <div className="text-center">
          <div className="text-5xl font-bold text-ux-violet mb-1">{gpa.gpa_9_scale}</div>
          <div className="text-lg font-medium">Grade: {gpa.grade}</div>
          <div className="text-sm text-gray-500">on {gpa.scale} scale</div>
        </div>
      );
    }
    
    return JSON.stringify(gpa);
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#1a2830]' : 'bg-slate-50'}`}>
      {/* Header */}
      <header className={`sticky top-0 z-50 border-b ${isDark ? 'bg-[#233842] border-white/10' : 'bg-white border-slate-200 shadow-sm'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/uac-desk/dashboard">
                <Button variant="ghost" size="icon">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-ux-violet to-purple-600 flex items-center justify-center">
                  <Calculator className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className={`font-bold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>GPA Calculator</h1>
                  <p className={`text-xs ${isDark ? 'text-white/50' : 'text-ux-charcoal/50'}`}>Convert your scores</p>
                </div>
              </div>
            </div>
            <Link to="/">
              <img src={getLogo()} alt="UniXperts" className={`h-8 w-auto ${isDark ? 'brightness-0 invert' : ''}`} />
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Info Banner */}
        <div className={`rounded-xl p-4 mb-6 flex items-start gap-3 ${isDark ? 'bg-ux-violet/10 border border-ux-violet/20' : 'bg-violet-50 border border-violet-100'}`}>
          <Info className="w-5 h-5 text-ux-violet flex-shrink-0 mt-0.5" />
          <div>
            <p className={`text-sm ${isDark ? 'text-white/80' : 'text-ux-charcoal/80'}`}>
              This calculator provides <strong>indicative GPA conversions</strong> based on standard formulas. 
              Actual requirements vary by university and program.
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-5 gap-6">
          {/* Input Form */}
          <div className="lg:col-span-3">
            <Card className={isDark ? 'bg-white/5 border-white/10' : ''}>
              <CardHeader>
                <CardTitle className={isDark ? 'text-white' : ''}>Enter Your Academic Details</CardTitle>
                <CardDescription className={isDark ? 'text-white/60' : ''}>
                  We'll convert your scores to your destination's grading system
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Level Selection */}
                <div className="space-y-3">
                  <Label className={isDark ? 'text-white' : ''}>I am applying for</Label>
                  <Tabs value={level} onValueChange={setLevel} className="w-full">
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="bachelors" className="flex items-center gap-2">
                        <GraduationCap className="w-4 h-4" />
                        Bachelor's
                      </TabsTrigger>
                      <TabsTrigger value="masters" className="flex items-center gap-2">
                        <Award className="w-4 h-4" />
                        Master's
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                  <p className={`text-xs ${isDark ? 'text-white/50' : 'text-ux-charcoal/50'}`}>
                    {level === 'bachelors' 
                      ? 'Converting Class 12 scores for undergraduate admission' 
                      : 'Converting Bachelor\'s degree scores for postgraduate admission'}
                  </p>
                </div>

                {/* Board Selection - FIRST for Bachelor's */}
                {level === 'bachelors' && (
                  <div className="space-y-3">
                    <Label className={isDark ? 'text-white' : ''}>Education Board</Label>
                    <Select value={board} onValueChange={handleBoardChange}>
                      <SelectTrigger className={isDark ? 'bg-white/5 border-white/20 text-white' : ''} data-testid="board-select">
                        <SelectValue placeholder="Select your board" />
                      </SelectTrigger>
                      <SelectContent>
                        {BOARDS.map((b) => (
                          <SelectItem key={b.id} value={b.id}>
                            <div className="flex flex-col">
                              <span>{b.name}</span>
                              <span className="text-xs text-gray-500">{b.description}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Score Input - Dynamic based on board */}
                <div className="space-y-3">
                  <Label className={isDark ? 'text-white' : ''}>
                    {level === 'bachelors' ? scoreConfig.label : 'Your Percentage / CGPA'}
                  </Label>
                  <div className="flex gap-3">
                    {level === 'bachelors' ? (
                      // Bachelor's: Board-specific input
                      scoreConfig.type === 'ib_points' ? (
                        <div className="flex-1">
                          <Input
                            type="number"
                            placeholder={scoreConfig.placeholder}
                            value={ibPoints}
                            onChange={(e) => setIbPoints(e.target.value)}
                            min={scoreConfig.min}
                            max={scoreConfig.max}
                            step={scoreConfig.step}
                            className={isDark ? 'bg-white/10 border-white/20' : ''}
                            data-testid="score-input"
                          />
                          <p className={`text-xs mt-1 ${isDark ? 'text-white/40' : 'text-ux-charcoal/40'}`}>
                            {scoreConfig.hint}
                          </p>
                        </div>
                      ) : (
                        <div className="flex-1">
                          <Input
                            type="number"
                            placeholder={scoreConfig.placeholder}
                            value={percentage}
                            onChange={(e) => setPercentage(e.target.value)}
                            min={scoreConfig.min}
                            max={scoreConfig.max}
                            step={scoreConfig.step}
                            className={isDark ? 'bg-white/10 border-white/20' : ''}
                            data-testid="score-input"
                          />
                          <p className={`text-xs mt-1 ${isDark ? 'text-white/40' : 'text-ux-charcoal/40'}`}>
                            {scoreConfig.hint}
                          </p>
                        </div>
                      )
                    ) : (
                      // Master's: Percentage or CGPA input
                      <>
                        <div className="flex-1">
                          <Input
                            type="number"
                            placeholder="e.g., 75 or 8.0"
                            value={percentage}
                            onChange={(e) => setPercentage(e.target.value)}
                            min="0"
                            max="100"
                            step="0.1"
                            className={isDark ? 'bg-white/10 border-white/20' : ''}
                            data-testid="score-input"
                          />
                          <p className={`text-xs mt-1 ${isDark ? 'text-white/40' : 'text-ux-charcoal/40'}`}>
                            Enter percentage (0-100) or CGPA converted to percentage
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {level === 'masters' && (
                  <div className="space-y-3">
                    <Label className={isDark ? 'text-white' : ''}>University Type</Label>
                    <Select value={universityTier} onValueChange={setUniversityTier}>
                      <SelectTrigger className={isDark ? 'bg-white/5 border-white/20 text-white' : ''} data-testid="university-select">
                        <SelectValue placeholder="Select university type" />
                      </SelectTrigger>
                      <SelectContent>
                        {UNIVERSITY_TIERS.map((t) => (
                          <SelectItem key={t.id} value={t.id}>
                            <div className="flex flex-col">
                              <span>{t.name}</span>
                              <span className="text-xs text-gray-500">{t.description}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className={`text-xs ${isDark ? 'text-white/40' : 'text-ux-charcoal/40'}`}>
                      Different institutions have varying grading standards
                    </p>
                  </div>
                )}

                {/* Destination */}
                <div className="space-y-3">
                  <Label className={isDark ? 'text-white' : ''}>Target Destination</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {DESTINATIONS.map((d) => (
                      <button
                        key={d.id}
                        type="button"
                        onClick={() => setDestination(d.id)}
                        className={`p-3 rounded-xl border-2 transition-all text-left ${
                          destination === d.id
                            ? 'border-ux-violet bg-ux-violet/10'
                            : isDark 
                              ? 'border-white/10 hover:border-white/30 bg-white/5' 
                              : 'border-gray-200 hover:border-gray-300 bg-white'
                        }`}
                        data-testid={`destination-${d.id}`}
                      >
                        <div className="text-xl mb-1">{d.flag}</div>
                        <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>{d.name}</div>
                        <div className={`text-xs ${isDark ? 'text-white/50' : 'text-ux-charcoal/50'}`}>{d.system}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Calculate Button */}
                <Button 
                  onClick={handleCalculate}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-ux-violet to-purple-600 hover:opacity-90 text-white h-12"
                  data-testid="calculate-btn"
                >
                  {loading ? (
                    <>
                      <Sparkles className="w-4 h-4 mr-2 animate-pulse" />
                      Calculating...
                    </>
                  ) : (
                    <>
                      <Calculator className="w-4 h-4 mr-2" />
                      Calculate GPA
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Results Panel */}
          <div className="lg:col-span-2">
            {result ? (
              <Card className={`sticky top-24 ${isDark ? 'bg-white/5 border-white/10' : ''}`}>
                <CardHeader className="pb-2">
                  <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
                    <Target className="w-5 h-5 text-ux-violet" />
                    Your {result.destination} GPA
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* GPA Display */}
                  <div className={`p-6 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gradient-to-br from-violet-50 to-purple-50'}`}>
                    {formatGPA(result.destination_gpa)}
                  </div>

                  {/* Competitiveness */}
                  <div className={`p-4 rounded-xl border ${getCompetitivenessColor(result.competitiveness.color)}`}>
                    <div className="flex items-start gap-3">
                      {result.competitiveness.tier === 'excellent' || result.competitiveness.tier === 'very_good' ? (
                        <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      ) : (
                        <TrendingUp className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      )}
                      <div>
                        <p className="font-semibold">{result.competitiveness.message}</p>
                        <p className="text-sm mt-1 opacity-80">
                          Target: {result.competitiveness.target_universities}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Conversion Details */}
                  <div className="space-y-2">
                    <p className={`text-xs font-medium uppercase tracking-wider ${isDark ? 'text-white/50' : 'text-ux-charcoal/50'}`}>
                      Conversion Details
                    </p>
                    <div className={`text-sm space-y-1 ${isDark ? 'text-white/70' : 'text-ux-charcoal/70'}`}>
                      <div className="flex justify-between">
                        <span>Input:</span>
                        <span className="font-medium">{result.input.value} ({result.input.type})</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Source:</span>
                        <span className="font-medium">{result.input.source}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Normalized %:</span>
                        <span className="font-medium">{result.normalized_percentage}%</span>
                      </div>
                      {result.conversion_details.adjustment_applied !== 0 && (
                        <div className="flex justify-between">
                          <span>Adjustment:</span>
                          <span className={`font-medium ${result.conversion_details.adjustment_applied > 0 ? 'text-emerald-600' : 'text-amber-600'}`}>
                            {result.conversion_details.adjustment_applied > 0 ? '+' : ''}{result.conversion_details.adjustment_applied}%
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Disclaimer */}
                  <div className={`p-3 rounded-lg text-xs ${isDark ? 'bg-amber-500/10 text-amber-200' : 'bg-amber-50 text-amber-700'}`}>
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <p>{result.disclaimer}</p>
                    </div>
                  </div>

                  {/* CTA */}
                  {fromAIFinder ? (
                    <Button 
                      className="w-full bg-gradient-to-r from-ux-cyan to-teal-500 hover:opacity-90 text-white"
                      onClick={() => {
                        // Get the scale from the selected destination
                        const selectedDest = DESTINATIONS.find(d => d.id === destination);
                        const scaleFromDest = selectedDest?.system || '4.0';
                        const scaleMatch = scaleFromDest.match(/[\d.]+/);
                        const numericScale = scaleMatch ? scaleMatch[0] : '4.0';
                        
                        // Extract the GPA value from destination_gpa object
                        let gpaValue = result.destination_gpa;
                        let extractedScale = numericScale;
                        
                        if (typeof gpaValue === 'object' && gpaValue !== null) {
                          // Check for UK/Ireland format (has gpa_equivalent like "3.7-4.0")
                          if (gpaValue.gpa_equivalent) {
                            const rangeMatch = gpaValue.gpa_equivalent.match(/([\d.]+)-([\d.]+)/);
                            if (rangeMatch) {
                              gpaValue = parseFloat(rangeMatch[1]); // Use lower end (conservative)
                            } else {
                              const singleMatch = gpaValue.gpa_equivalent.match(/[\d.]+/);
                              gpaValue = singleMatch ? parseFloat(singleMatch[0]) : null;
                            }
                            extractedScale = '4';
                          } else {
                            // Standard numeric GPA objects - extract value AND set correct scale
                            if (gpaValue.gpa_4_scale !== undefined) {
                              gpaValue = gpaValue.gpa_4_scale;
                              extractedScale = '4';
                            } else if (gpaValue.gpa_7_scale !== undefined) {
                              gpaValue = gpaValue.gpa_7_scale;
                              extractedScale = '7';
                            } else if (gpaValue.gpa_9_scale !== undefined) {
                              gpaValue = gpaValue.gpa_9_scale;
                              extractedScale = '9';
                            } else if (gpaValue.german_grade !== undefined) {
                              gpaValue = gpaValue.german_grade;
                              extractedScale = '5'; // German uses 1.0-5.0 scale
                            } else if (gpaValue.gpa !== undefined) {
                              gpaValue = gpaValue.gpa;
                              // Keep extractedScale from destination
                            } else {
                              gpaValue = Object.values(gpaValue).find(v => typeof v === 'number');
                            }
                          }
                        }
                        
                        // Save GPA data and navigate back to AI Finder
                        if (gpaValue && !isNaN(gpaValue)) {
                          localStorage.setItem('calculated_gpa', JSON.stringify({
                            gpa: gpaValue,
                            scale: extractedScale,
                            percentage: result.normalized_percentage,
                            destination: result.destination
                          }));
                        }
                        navigate('/uac-desk/ai-finder');
                      }}
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Use This GPA in AI Finder
                    </Button>
                  ) : (
                    <Link to="/uac-desk/ai-finder">
                      <Button className="w-full" variant="outline">
                        Find Matching Universities
                        <ChevronRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card className={`sticky top-24 ${isDark ? 'bg-white/5 border-white/10' : ''}`}>
                <CardContent className="p-8 text-center">
                  <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center ${isDark ? 'bg-white/10' : 'bg-gray-100'}`}>
                    <Calculator className={`w-8 h-8 ${isDark ? 'text-white/40' : 'text-gray-400'}`} />
                  </div>
                  <h3 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                    Enter Your Scores
                  </h3>
                  <p className={`text-sm ${isDark ? 'text-white/50' : 'text-ux-charcoal/50'}`}>
                    Fill in your academic details to see your converted GPA and university match potential
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default GPACalculatorPage;
